import React, { useRef, useState, useContext, useEffect } from 'react'
import { ThemeContext } from 'styled-components'
import Measure, { ContentRect } from 'react-measure'
import { useLocation } from 'react-router-dom'

import {
  FullPageLayout,
  Wrapper,
  Sidebar,
  Content,
  HeaderContainer,
  ContentContainer,
} from './SplitLayout.styles'

import useOnClickOutside, {
  DROPDOWN_SELECTOR,
  MODAL_SELECTOR,
} from 'utils/hooks/useOnClickOutside'

import { callIfFunction, RenderFunctionOrNode } from '../utils'

/**
 * TYPES
 */
interface SplitLayoutProps {
  title?: string
  /** Left sidebar, for navigation */
  sidebar?: RenderFunctionOrNode
  header: RenderFunctionOrNode
  footer: RenderFunctionOrNode
  children?: RenderFunctionOrNode
  headerHeight?: number
}

/**
 * COMPONENT
 * Used for documents view (homepage), it provides a left sidebar to show the
 * main navigation
 */
const SplitLayout: React.FC<SplitLayoutProps> = ({
  title,
  sidebar,
  header,
  footer,
  children,
}) => {
  const theme = useContext(ThemeContext)
  const [headerHeight, setHeaderHeight] = useState<number>(theme.header.height)

  // Only used for mobile
  const [isSidebarLeftOpen, setSidebarLeftOpen] = useState(false)

  const closeSidebar = () => {
    if (isSidebarLeftOpen) {
      // setTimeout is needed to make sure `setSidebarLeftOpen` is called after
      // any other click event listener, for example the hamburger icon click
      setTimeout(() => setSidebarLeftOpen(false), 0)
    }
  }

  // Close the left sidebar when the user clicks outside of it
  const sidebarRef = useRef<HTMLElement>(null)
  useOnClickOutside(sidebarRef, closeSidebar, {
    includeSelectors: [MODAL_SELECTOR, DROPDOWN_SELECTOR],
  })

  // Make sure the sidebar is closed every time the route changes
  const location = useLocation()
  useEffect(() => {
    closeSidebar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  /*
   * Watching header height changes to reposition <Sidebar/>
   * and <PageContent /> when banners are displayed.
   */
  const handleResize = ({ entry }: ContentRect) => {
    if (!entry) {
      return
    }

    // Prevent setting a new state when the value is the same
    if (headerHeight === entry.height) {
      return
    }

    setHeaderHeight(entry.height)
  }

  return (
    <FullPageLayout title={title} showHeader={false}>
      <Wrapper>
        {sidebar && (
          <Sidebar ref={sidebarRef} isSidebarOpen={isSidebarLeftOpen}>
            {callIfFunction(sidebar)}
          </Sidebar>
        )}

        <Content>
          <Measure onResize={handleResize}>
            {({ measureRef }) => (
              <HeaderContainer ref={measureRef}>
                {callIfFunction(header, {
                  isSidebarLeftOpen,
                  setSidebarLeftOpen,
                })}
              </HeaderContainer>
            )}
          </Measure>

          <ContentContainer
            style={{ marginTop: `${headerHeight}px` }}
            isSidebarOpen={isSidebarLeftOpen}>
            {callIfFunction(children)}
          </ContentContainer>
          {callIfFunction(footer)}
        </Content>
      </Wrapper>
    </FullPageLayout>
  )
}

export { SplitLayout }
